import React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

// ~8 lines of body copy at the feed's line-height (14px text, 1.35).
const CLAMP_PX = 168;

/**
 * Collapses a long post body to ~8 lines with a soft fade and a Read more toggle.
 *
 * Clamps by rendered HEIGHT, not character count: 900 characters of one paragraph and
 * 900 characters of bullets occupy wildly different space. The toggle only appears when
 * the content really overflows, re-measured when images/fonts settle.
 *
 * Only the TEXT is clamped — link preview cards and media are siblings of this component,
 * so they always render in full however long the post is.
 */
export const ClampedBody = ({ children, testId, expanded: forceExpanded = false }) => {
  const ref = React.useRef(null);
  const [open, setOpen] = React.useState(forceExpanded);
  const [overflows, setOverflows] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || forceExpanded) return;
    const measure = () => setOverflows(el.scrollHeight > CLAMP_PX + 8);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children, forceExpanded]);

  const clamped = overflows && !open;

  return (
    <div data-testid={testId}>
      <div
        ref={ref}
        className="relative overflow-hidden"
        style={clamped ? { maxHeight: `${CLAMP_PX}px` } : undefined}
      >
        {children}
        {clamped && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-b from-transparent to-card" />
        )}
      </div>
      {overflows && !forceExpanded && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="mt-1 inline-flex items-center gap-1 text-xs font-[640] text-primary transition-colors hover:text-primary/80"
          data-testid={`${testId}-toggle`}
        >
          {open ? <>Show less <ChevronUp className="h-3.5 w-3.5" /></>
                : <>Read more <ChevronDown className="h-3.5 w-3.5" /></>}
        </button>
      )}
    </div>
  );
};

export default ClampedBody;
